import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { OutboundSystemView } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  findDefaultOutboundView,
  OutboundViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { isSupportTicketRouteId } from "@redotech/redo-merchant-links/links";
import { memo, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { storePaths } from "./store-paths";

export const MerchantRouteNavigate = memo(function MerchantRoute({
  routeTo,
}: {
  routeTo: (routes: MerchantRoutes) => string;
}) {
  const navigate = useNavigate();
  const routes = useContext(MerchantRoutesContext);

  useEffect(() => {
    if (!routes) {
      return;
    }
    const href = routeTo(routes);
    navigate(href, { replace: true });
  }, [navigate, routes, routeTo]);

  return null;
});

/**
 * Decides where a user lands when they open the Shipping section.
 *
 * Admins land on the analytics dashboard. Everyone else is dropped directly
 * into the "Ready to ship" fulfillment group (the awaiting-shipment view),
 * which also auto-expands the Fulfillment nav group since it's a fulfillment
 * child route. Permission-based fallbacks are preserved: teams without OMS
 * see the marketing/upsell page, and scan-only warehouse users still land on
 * the scan page.
 */
export function getShippingDefaultRoute({
  shippingBaseUrl,
  omsEnabled,
  isAdmin,
  canViewDashboard,
  canManageShipments,
  canScanAndVerify,
  readyToShipViewName,
}: {
  shippingBaseUrl: string;
  omsEnabled: boolean;
  isAdmin: boolean;
  canViewDashboard: boolean;
  canManageShipments: boolean;
  canScanAndVerify: boolean;
  readyToShipViewName: string;
}): string {
  const aboutRoute = `${shippingBaseUrl}/outbound-labels/about`;
  const dashboardRoute = `${shippingBaseUrl}/outbound-labels/dashboard`;
  // The "Ready to ship" landing is whichever fulfillment-group view the team
  // uses as their default. Teams can rename the system awaiting-shipment view,
  // so the slug is resolved from the team's actual views at the caller; we
  // just URL-encode it here. Landing on this view also auto-expands the
  // Fulfillment nav group since it's a fulfillment child route.
  const readyToShipRoute = `${shippingBaseUrl}/outbound-labels/${encodeURIComponent(readyToShipViewName)}`;
  const scanRoute = `${shippingBaseUrl}/outbound-labels/scan`;

  // When OMS is not enabled, land on the marketing/upsell page instead of the
  // (empty) fulfillment dashboard. Teams that have returns enabled can still
  // reach Carrier claims via the secondary nav.
  if (!omsEnabled) {
    return aboutRoute;
  }
  // Admins land on the analytics dashboard. Non-admins are dropped directly
  // into the "Ready to ship" fulfillment group instead.
  if (isAdmin && canViewDashboard) {
    return dashboardRoute;
  }
  if (canManageShipments) {
    return readyToShipRoute;
  }
  if (canScanAndVerify) {
    return scanRoute;
  }
  return readyToShipRoute;
}

interface ConciergeDefaultRouteInput {
  supportV2BaseUrl: string;
  enabled: boolean;
}

export function getConciergeDefaultRoute({
  supportV2BaseUrl,
  enabled,
}: ConciergeDefaultRouteInput): string {
  return enabled
    ? `${supportV2BaseUrl}/dashboard`
    : `${supportV2BaseUrl}/concierge/about`;
}

export const MerchantRoutesProvider = memo(function MerchantRoutesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useContext(AuthContext);
  const team = useContext(TeamContext);
  const user = useContext(UserContext);

  const isAdmin =
    user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;

  const omsEnabled = useCapability("oms");
  const warrantiesEnabled = useCapability("warranties");
  const permissions = useContext(PermissionsContext);
  const outboundViews = useContext(OutboundViewsContext);

  // Drop non-admins onto whichever fulfillment-group view sits visually at
  // the top of the team's nav — folders and top-level views together by
  // position, diving into the first folder if one sorts above any top-level
  // view. Matches what the merchant actually sees, so teams who rename,
  // reorder, or replace the system awaiting-shipment view still land
  // somewhere real instead of the create-view editor. While views are still
  // loading, fall back to the system slug — once views resolve the nav href
  // updates.
  const readyToShipViewName = useMemo(() => {
    if (!outboundViews?.views || outboundViews.views.length === 0) {
      return OutboundSystemView.AWAITING_SHIPMENT;
    }
    const first = findDefaultOutboundView({
      views: outboundViews.views,
      folders: outboundViews.folders ?? [],
    });
    return first?.name ?? OutboundSystemView.AWAITING_SHIPMENT;
  }, [outboundViews]);

  const teamId = auth?.teamId;

  const paths = storePaths(teamId ? `/stores/${teamId}` : "");
  const baseUrl = paths.base;
  const storeBaseUrl = paths.base;
  const homeBaseHref = paths.home;
  const shippingBaseUrl = paths.shipping;
  const returnsBaseUrl = paths.returns;
  const inventoryManagementBaseUrl = paths.inventory;
  const catalogBaseUrl = paths.catalog;
  const marketingBaseUrl = paths.marketing;
  const legacySupportBaseUrl = paths.support;
  const supportV2BaseUrl = paths.supportV2;
  const faqBaseUrl = paths.faq;
  const supportBaseUrl = supportV2BaseUrl;
  const conversionBaseUrl = paths.conversion;
  const customersBaseUrl = paths.customers;
  const paymentsBaseUrl = paths.payments;
  const redoSuppliesBaseUrl = paths.redoSupplies;
  const aeoBaseUrl = paths.aeo;
  const analyticsBaseUrl = paths.analytics;
  const developerBaseUrl = paths.developer;

  const claimsHref = `${returnsBaseUrl}/claims`;

  const shippingDefaultRoute = useMemo(
    () =>
      getShippingDefaultRoute({
        shippingBaseUrl,
        omsEnabled: !!omsEnabled,
        isAdmin,
        canViewDashboard: !!permissions?.has(
          OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        ),
        canManageShipments: !!permissions?.has(
          OrderManagementPermissions.MANAGE_SHIPMENTS,
        ),
        canScanAndVerify: !!permissions?.has(
          OrderManagementPermissions.SCAN_AND_VERIFY,
        ),
        readyToShipViewName,
      }),
    [permissions, omsEnabled, shippingBaseUrl, isAdmin, readyToShipViewName],
  );

  const returnsDefaultRoute = useMemo(() => {
    const canViewReturns = permissions?.has(
      ReturnsAndClaimsPermissions.VIEW_RETURN,
    );
    const canManageWarranties = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    );

    if (canViewReturns) {
      return `${returnsBaseUrl}/returns`;
    } else if (canManageWarranties && warrantiesEnabled) {
      return `${returnsBaseUrl}/warranties`;
    }
    return `${returnsBaseUrl}/returns`;
  }, [permissions, warrantiesEnabled, returnsBaseUrl]);

  const claimsDefaultRoute = useMemo(() => {
    const canManagePackageProtectionClaims = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    );

    const canManageCarrierClaims = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
    );

    const packageProtectionEnabled =
      !!team?.settings.packageProtection?.enabled;

    const omsEnabled = !!team?.settings.outboundLabels?.newOmsEnabled;
    const isReturnEnabled = team?.settings.returns?.enabled;

    if (canManagePackageProtectionClaims && packageProtectionEnabled) {
      return `${claimsHref}/all`;
    } else if (canManageCarrierClaims && (omsEnabled || isReturnEnabled)) {
      return `${claimsHref}/carrier-claims`;
    } else {
      return `${claimsHref}/all`;
    }
  }, [team?.settings, claimsHref, permissions]);

  const merchantRoutes: MerchantRoutes = useMemo(
    () => ({
      // Home routes
      overviewHref: `${homeBaseHref}/overview`,
      analyticsHref: `${homeBaseHref}/analytics`,
      getAnalyticsSubRoute: (subPath: string) =>
        `${homeBaseHref}/analytics/${subPath}`,
      homeBaseHref,

      // Shipping routes
      shippingDefaultRoute,
      pickSessionHref: `${shippingBaseUrl}/outbound-labels/pick-sessions`,
      pickSessionTableHref: `${shippingBaseUrl}/outbound-labels/admin-pick-sessions`,
      transferHref: `${shippingBaseUrl}/warehouse/transfer`,
      instantTransferHref: `${shippingBaseUrl}/warehouse/instant-transfer`,
      receiveToBinHref: `${shippingBaseUrl}/warehouse/receive-to-bin`,
      consolidationHref: `${shippingBaseUrl}/warehouse/consolidation`,
      consolidationsHref: `${shippingBaseUrl}/consolidations`,
      flushHref: `${shippingBaseUrl}/warehouse/flush`,

      ordersOutboundLabelsHref: `${shippingBaseUrl}/outbound-labels`,
      getOrdersOutboundLabelsSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/outbound-labels/${subPath}`,

      warehouseHref: `${shippingBaseUrl}/warehouse`,
      warehouseBaseUrl: `${shippingBaseUrl}/warehouse`,

      orderEditsHref: `${shippingBaseUrl}/outbound-labels/order-edits`,
      analyticsOrderFulfillmentHref: `${shippingBaseUrl}/analytics/order-fulfillment`,
      getAnalyticsOrderFulfillmentSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/analytics/order-fulfillment?tab=${subPath}`,
      shippingCarrierClaimsHref: `${shippingBaseUrl}/carrier-claims`,

      // Returns routes
      returnsDefaultRoute,
      returnsHref: `${returnsBaseUrl}/returns`,
      getReturnsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/returns/${subPath}`,

      claimsHref,
      claimsDefaultRoute,
      getClaimsSubRoute: (subPath: string) => `${claimsHref}/${subPath}`,

      getWarrantiesHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${returnsBaseUrl}/warranties/all-warranties`
          : `${returnsBaseUrl}/warranties/about`,
      warrantiesHref: `${returnsBaseUrl}/warranties`,
      getWarrantiesSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/warranties/${subPath}`,
      registrationsHref: `${returnsBaseUrl}/registrations`,

      returnsProcessingHref: `${shippingBaseUrl}/warehouse/processing`,

      getRecycleHref: () => `${returnsBaseUrl}/recycle/all-recycle`,
      recycleHref: `${returnsBaseUrl}/recycle`,
      getRecycleSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/recycle/${subPath}`,

      getWithdrawalHref: () => `${returnsBaseUrl}/withdrawals/all-withdrawals`,
      withdrawalHref: `${returnsBaseUrl}/withdrawals`,
      getWithdrawalSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/withdrawals/${subPath}`,

      fraudHref: `${returnsBaseUrl}/fraud`,
      getFraudSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/fraud/${subPath}`,

      analyticsReturnsClaimsHref: `${returnsBaseUrl}/analytics/returns-claims`,
      getAnalyticsReturnsClaimsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/analytics/returns-claims/${subPath}`,
      analyticsReportsHref: `${returnsBaseUrl}/analytics/reports`,
      analyticsManagedReturnsHref: `${returnsBaseUrl}/analytics/managed-returns`,
      managedReturnsHref: `${returnsBaseUrl}/managed-returns`,
      getManagedReturnsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/managed-returns/${subPath}`,
      warehouseMetricsHref: `${shippingBaseUrl}/wms/warehouse-metrics`,
      warehouseSlasHref: `${shippingBaseUrl}/wms/warehouse-metrics/warehouse-slas`,
      inboundDashboardHref: `${shippingBaseUrl}/wms/warehouse-metrics/inbound-dashboard`,
      processingPipelineHref: `${shippingBaseUrl}/wms/warehouse-metrics/processing-pipeline`,
      qaMetricsHref: `${shippingBaseUrl}/wms/warehouse-metrics/qa-metrics`,
      refulfillmentHref: `${shippingBaseUrl}/wms/warehouse-metrics/refulfillment`,

      // Inventory routes
      getInventoryHref: ({ enabled }: { enabled: boolean }) =>
        `${inventoryManagementBaseUrl}`,

      inventoryProductsHref: `${catalogBaseUrl}/products`,
      catalogProductsHref: `${catalogBaseUrl}/products`,
      inventoryAnalyticsReportsHref: `${inventoryManagementBaseUrl}/analytics/reports`,

      // Marketing routes
      marketingAnalyticsHref: `${marketingBaseUrl}/analytics`,
      marketingAnalyticsReportsHref: `${marketingBaseUrl}/analytics/reports`,
      automationsHref: `${marketingBaseUrl}/automations`,
      marketingPopupsHref: `${marketingBaseUrl}/email-sms/popups`,
      getMarketingPopupsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/email-sms/popups/${subPath}`,

      getTrackingHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${marketingBaseUrl}/tracking`
          : `${marketingBaseUrl}/tracking/about`,

      getTrackingSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/tracking/${subPath}`,

      recoverHref: `${marketingBaseUrl}/recover`,
      getRecoverSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/recover/${subPath}`,

      contextualHref: `${marketingBaseUrl}/contextual`,
      getContextualSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/contextual/${subPath}`,

      reviewsHref: `${marketingBaseUrl}/reviews/dashboard`,
      getReviewsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/reviews/${subPath}`,

      emailSmsHref: `${marketingBaseUrl}/email-sms`,
      getEmailSmsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/email-sms/${subPath}`,

      discountsHref: `${marketingBaseUrl}/discounts`,
      getDiscountsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/discounts/${subPath}`,

      loyaltyHref: `${marketingBaseUrl}/loyalty/program`,
      getLoyaltySubRoute: (subPath: string) =>
        `${marketingBaseUrl}/loyalty/${subPath}`,

      marketingAgentHref: `${marketingBaseUrl}/marketing-agent`,
      getMarketingAgentSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/marketing-agent/${subPath}`,

      shopperAgentHref: `${baseUrl}/commerce-agent`,
      getShopperAgentSubRoute: (subPath: string) =>
        `${baseUrl}/commerce-agent/${subPath}`,

      textConciergeHref: `${marketingBaseUrl}/text-concierge`,
      getTextConciergeSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/text-concierge/${subPath}`,

      // Support routes
      getSupportHref: ({ enabled }: { enabled: boolean }) => {
        return enabled ? supportV2BaseUrl : `${supportV2BaseUrl}/about`;
      },

      getConversationHref: (conversationId: string) => {
        return isSupportTicketRouteId(conversationId)
          ? `${supportV2BaseUrl}/${conversationId}`
          : supportV2BaseUrl;
      },
      getConciergeHref: ({ enabled }: { enabled: boolean }) =>
        getConciergeDefaultRoute({ supportV2BaseUrl, enabled }),
      getConciergeSubRoute: (subPath: string) => {
        return `${supportV2BaseUrl}/concierge/${subPath}`;
      },
      getFaqArticleEditorHref: (faqArticleId: string) => {
        return `${faqBaseUrl}/editor/${encodeURIComponent(faqArticleId)}`;
      },

      // Conversion routes
      popupsHref: `${conversionBaseUrl}/popups`,
      getConversionPopupsSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/popups/${subPath}`,
      spotlightsHref: `${conversionBaseUrl}/spotlights`,
      getSpotlightsSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/spotlights/${subPath}`,
      conversionAnalyticsHref: `${conversionBaseUrl}/analytics`,
      conversionAnalyticsReportsHref: `${conversionBaseUrl}/analytics/reports`,
      getConversionHref: ({
        enabled,
        checkoutOutboundRatesEnabled,
      }: {
        enabled: boolean;
        checkoutOutboundRatesEnabled: boolean;
      }) => {
        if (enabled && checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/dashboard`;
        } else if (enabled && !checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/dashboard`;
        } else {
          return `${conversionBaseUrl}/welcome`;
        }
      },

      // Marketing routes
      getMarketingHref: ({
        emailSmsEnabled,
        marketingSectionEnabled,
      }: {
        emailSmsEnabled: boolean;
        marketingSectionEnabled: boolean;
      }) => {
        if (emailSmsEnabled || marketingSectionEnabled) {
          return marketingBaseUrl;
        }
        return `${marketingBaseUrl}/welcome`;
      },

      getSegmentBuilderRoute({
        teamId,
        segmentId,
      }: {
        teamId: string;
        segmentId: string;
      }) {
        return `${storeBaseUrl}/marketing/segments/${segmentId}`;
      },

      getCustomerRecordHref: ({ customerId }: { customerId?: string }) =>
        customerId
          ? `${customersBaseUrl}/${customerId}`
          : `${customersBaseUrl}/all-customers`,

      getConversionSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/${subPath}`,

      // Settings routes
      settingsHref: `${storeBaseUrl}/settings`,
      getSettingsSubRoute: (subPath: string) =>
        `${storeBaseUrl}/settings/${subPath}`,

      // Base URL helpers
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      catalogBaseUrl,
      marketingBaseUrl,
      legacySupportBaseUrl,
      supportV2BaseUrl,
      supportBaseUrl,
      faqBaseUrl,
      conversionBaseUrl,
      storeBaseUrl,
      customersBaseUrl,
      aeoBaseUrl,

      // Payments routes
      paymentsBaseUrl,
      chargebacksHref: `${paymentsBaseUrl}/chargebacks`,
      chargebacksOverviewHref: `${paymentsBaseUrl}/chargebacks/overview`,
      chargebacksReclaimHref: `${paymentsBaseUrl}/chargebacks/reclaim`,
      chargebacksAlertsHref: `${paymentsBaseUrl}/chargebacks/alerts`,
      chargebacksResolveHref: `${paymentsBaseUrl}/chargebacks/resolve`,
      paymentsHsaHref: `${paymentsBaseUrl}/hsa`,
      paymentsHsaQuestionnaireHref: `${paymentsBaseUrl}/hsa/questionnaire`,
      paymentsHsaTransactionsHref: `${paymentsBaseUrl}/hsa/transactions`,
      paymentsHsaPayoutsHref: `${paymentsBaseUrl}/hsa/payouts`,

      // Redo Supplies routes
      redoSuppliesBaseUrl,
      redoSuppliesShopHref: `${redoSuppliesBaseUrl}/shop`,
      redoSuppliesOrderHistoryHref: `${redoSuppliesBaseUrl}/order-history`,
      redoSuppliesRecurringPurchasesInventoryHref: `${redoSuppliesBaseUrl}/recurring-purchases/inventory`,
      redoSuppliesRecurringPurchasesTimeHref: `${redoSuppliesBaseUrl}/recurring-purchases/time`,

      // Top-level Analytics routes
      analyticsBaseUrl,
      analyticsOverviewHref: `${analyticsBaseUrl}/home`,
      analyticsCustomReportsHref: `${analyticsBaseUrl}/reports`,

      // AEO routes
      aeoDashboardHref: `${aeoBaseUrl}/dashboard`,
      aeoIntegrationsHref: `${aeoBaseUrl}/integrations`,
      aeoPromptsHref: `${aeoBaseUrl}/prompts`,
      aeoCatalogHref: `${aeoBaseUrl}/catalog`,
      aeoAssistantHref: `${aeoBaseUrl}/assistant`,
      aeoCompetitorsHref: `${aeoBaseUrl}/competitors`,
      aeoBrandHref: `${aeoBaseUrl}/brand`,
      getAeoSubRoute: (subPath: string) => `${aeoBaseUrl}/${subPath}`,
      getAeoCatalogSubRoute: (subPath: string) =>
        `${aeoBaseUrl}/catalog/${subPath}`,

      // Developer portal routes
      developerBaseUrl,
      developerFunctionsHref: `${developerBaseUrl}/functions`,
      developerLogsHref: `${developerBaseUrl}/logs`,
      developerAutomationsHref: `${developerBaseUrl}/automations`,
    }),
    [
      homeBaseHref,
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      catalogBaseUrl,
      marketingBaseUrl,
      aeoBaseUrl,
      analyticsBaseUrl,
      developerBaseUrl,
      paymentsBaseUrl,
      redoSuppliesBaseUrl,
      legacySupportBaseUrl,
      supportV2BaseUrl,
      supportBaseUrl,
      faqBaseUrl,
      conversionBaseUrl,
      customersBaseUrl,
      storeBaseUrl,
      baseUrl,
      shippingDefaultRoute,
      returnsDefaultRoute,
      claimsHref,
      claimsDefaultRoute,
    ],
  );

  return (
    <MerchantRoutesContext.Provider value={merchantRoutes}>
      {children}
    </MerchantRoutesContext.Provider>
  );
});
